import React from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import About from './pages/About';
import Services from './pages/Services';
import Packages from './pages/Packages';
import Gallery from './pages/Gallery';
import Testimonials from './pages/Testimonials';
import Contact from './pages/Contact';
import Membership from './pages/Membership';
import Booking from './pages/Booking';
import Promotion from './pages/Promotion';
import JoinPage from './pages/JoinPage';
import JoinSuccessPage from './pages/JoinSuccessPage';
import CircularGallery from './components/CircularGallery';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import PrivacyPolicy from './pages/PrivacyPolicy';
import FacialConsentFormPage from './pages/public/FacialConsentFormPage';
import TermsOfService from './pages/TermsOfService';
import ProfilePage from './pages/customer/ProfilePage';
import ReceiptPage from './pages/receipt/ReceiptPage';
import Dashboard from './pages/admin/Dashboard';
import UserManagement from './pages/admin/UserManagement';
import ContentManagement from './pages/admin/ContentManagement';
import SchedulingSystem from './pages/admin/SchedulingSystem';
import TeamManagement from './pages/admin/TeamManagement';
import TimeReports from './pages/admin/TimeReports';
import ServiceReports from './pages/admin/ServiceReports';
import AdminLayout from './components/admin/AdminLayout';
import CrewLayout from './components/crew/CrewLayout';
import CrewDashboard from './pages/crew/CrewDashboard';
import CrewSchedule from './pages/crew/CrewSchedule';
import CrewProfile from './pages/crew/CrewProfile';
import { AuthProvider } from './contexts/auth/AuthContext';
import { ServiceProvider } from './contexts/services/ServiceContext';
import { NotificationProvider } from './contexts/notifications/NotificationContext';
import { AskLotusProvider } from './contexts/askLotus/AskLotusContext';
import ProtectedRoute from './contexts/auth/ProtectedRoute';
import AdminRoute from './contexts/auth/AdminRoute';
import CrewRoute from './contexts/auth/CrewRoute';
import ScrollToTop from './components/ScrollToTop';
import AskLotusWidget from './components/askLotus/AskLotusWidget';
import Messages from './pages/admin/Messages';
import WalletManagement from './pages/admin/WalletManagement';

// Main App component with routes and layout
function App() {
  const location = useLocation();
  const isAdminOrCrew = location.pathname.includes('/admin') || location.pathname.includes('/crew');

  return (
    <AuthProvider>
      <ServiceProvider>
      <NotificationProvider>
      <AskLotusProvider>
      <ScrollToTop />
      <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] font-sans flex flex-col overflow-x-hidden">
        {/* Navigation Bar */}
        <Navbar />
        {/* Main content with top padding for fixed navbar */}
        <div className="pt-20 flex-1 flex flex-col">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/packages" element={<Packages />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/membership" element={<Membership />} />
            <Route path="/gift-card" element={<Navigate to="/membership" replace />} />
            <Route path="/promotion" element={<Promotion />} />
            <Route path="/booking" element={<Booking />} />

            {/* Membership Routes */}
            <Route path="/join" element={
              <ProtectedRoute>
                <JoinPage />
              </ProtectedRoute>
            } />
            <Route path="/join-success" element={
              <ProtectedRoute>
                <JoinSuccessPage />
              </ProtectedRoute>
            } />

            {/* Auth routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />

            {/* Legal pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />

            {/* Consent Form (public - accessible via token or logged in) */}
            <Route path="/consent-form" element={<FacialConsentFormPage />} />

            {/* Customer Profile */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />

            {/* Receipt Page - Public (receipt ID serves as access token) */}
            <Route path="/receipt/:id" element={<ReceiptPage />} />

            {/* Admin Routes */}
            <Route path="/admin" element={
              <AdminRoute>
                <AdminLayout>
                  <Dashboard />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/dashboard" element={
              <AdminRoute>
                <AdminLayout>
                  <Dashboard />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/users" element={
              <AdminRoute>
                <AdminLayout>
                  <UserManagement />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/content" element={
              <AdminRoute>
                <AdminLayout>
                  <ContentManagement />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/team" element={
              <AdminRoute>
                <AdminLayout>
                  <TeamManagement />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/schedule" element={
              <AdminRoute>
                <AdminLayout>
                  <SchedulingSystem />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/time-reports" element={
              <AdminRoute>
                <AdminLayout>
                  <TimeReports />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/service-reports" element={
              <AdminRoute>
                <AdminLayout>
                  <ServiceReports />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/messages" element={
              <AdminRoute>
                <AdminLayout>
                  <Messages />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/wallet-management" element={
              <AdminRoute>
                <AdminLayout>
                  <WalletManagement />
                </AdminLayout>
              </AdminRoute>
            } />

            {/* Crew Routes */}
            <Route path="/crew" element={
              <CrewRoute>
                <CrewLayout>
                  <CrewDashboard />
                </CrewLayout>
              </CrewRoute>
            } />
            <Route path="/crew/dashboard" element={
              <CrewRoute>
                <CrewLayout>
                  <CrewDashboard />
                </CrewLayout>
              </CrewRoute>
            } />
            <Route path="/crew/schedule" element={
              <CrewRoute>
                <CrewLayout>
                  <CrewSchedule />
                </CrewLayout>
              </CrewRoute>
            } />
            <Route path="/crew/profile" element={
              <CrewRoute>
                <CrewLayout>
                  <CrewProfile />
                </CrewLayout>
              </CrewRoute>
            } />
            <Route path="/crew/messages" element={
              <CrewRoute>
                <CrewLayout>
                  <Messages />
                </CrewLayout>
              </CrewRoute>
            } />
            <Route path="/crew/users" element={
              <CrewRoute>
                <CrewLayout>
                  <UserManagement mode="crew" />
                </CrewLayout>
              </CrewRoute>
            } />
          </Routes>
          {/* Ask Lotus Chat Widget */}
          <AskLotusWidget />
          {/* Footer - Only show on main site, not admin or crew */}
          {!isAdminOrCrew && (
            <footer className="w-full bg-gradient-to-b from-[#e8f5f2] to-[#b2d8c5]/30 text-[#232347] pt-16 pb-8 px-4 mt-auto">
              <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-12 md:gap-0 justify-between items-start md:items-stretch">
                {/* Left: Social & Brand */}
                <div className="flex-1 flex flex-col gap-6 mb-10 md:mb-0">
                  <div className="flex gap-4 text-2xl">
                    <a href="#" aria-label="Instagram" className="hover:text-[#B2D8C5] transition"><i className="fab fa-instagram" />📸</a>
                    <a href="#" aria-label="Facebook" className="hover:text-[#B2D8C5] transition"><i className="fab fa-facebook" />📘</a>
                    <a href="#" aria-label="X" className="hover:text-[#B2D8C5] transition"><i className="fab fa-x-twitter" />✖️</a>
                  </div>
                  <div className="mt-4 text-lg font-serif italic text-[#232347]/80">Relax, recharge, and renew—your wellness journey starts here.</div>
                </div>
                {/* Right: Link Groups */}
                <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-12">
                  <div>
                    <div className="uppercase text-xs font-bold tracking-widest mb-3 text-[#232347]/70">Quick Links</div>
                    <ul className="space-y-2">
                      <li><Link to="/services" className="hover:text-[#B2D8C5] transition">Services</Link></li>
                      <li><Link to="/booking" className="hover:text-[#B2D8C5] transition">Book Appointment</Link></li>
                      <li><Link to="/membership" className="hover:text-[#B2D8C5] transition">Membership</Link></li>
                    </ul>
                  </div>
                  <div>
                    <div className="uppercase text-xs font-bold tracking-widest mb-3 text-[#232347]/70">About</div>
                    <ul className="space-y-2">
                      <li><Link to="/about" className="hover:text-[#B2D8C5] transition">About Us</Link></li>
                      <li><Link to="/gallery" className="hover:text-[#B2D8C5] transition">Gallery</Link></li>
                      <li><Link to="/testimonials" className="hover:text-[#B2D8C5] transition">Testimonials</Link></li>
                      <li><Link to="/contact" className="hover:text-[#B2D8C5] transition">Contact</Link></li>
                    </ul>
                  </div>
                  <div>
                    <div className="uppercase text-xs font-bold tracking-widest mb-3 text-[#232347]/70">Legal</div>
                    <ul className="space-y-2">
                      <li><Link to="/privacy-policy" className="hover:text-[#B2D8C5] transition">Privacy Policy</Link></li>
                      <li><Link to="/terms-of-service" className="hover:text-[#B2D8C5] transition">Terms of Service</Link></li>
                    </ul>
                    <div className="uppercase text-xs font-bold tracking-widest mt-6 mb-3 text-[#232347]/70">Location</div>
                    <ul className="space-y-2">
                      <li>
                        <a
                          href="https://maps.google.com/?q=4222+Trinity+Mills+Rd+Suite+120,+Dallas,+TX+75024"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-[#B2D8C5] transition"
                        >
                          4222 Trinity Mills Rd<br />Suite 120<br />Dallas, TX 75024
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="text-center text-[#B2D8C5] mt-12 text-sm font-serif">© 2026 Healing Lotus. All rights reserved.</div>
            </footer>
          )}
        </div>
      </div>
      </AskLotusProvider>
      </NotificationProvider>
      </ServiceProvider>
    </AuthProvider>
  );
}

export default App;
